// Cálculo del presupuesto de oferta (misma lógica que la plantilla Excel
// "Ejemplo CIEC"). JS plano compartido por las Edge Functions y la app.
//
// Partida (row):
//   concept            texto libre (lo que pide el pliego)
//   units              Uds/h
//   tariffId           código de la partida del tarifario interno o ''
//   tariffSnap         { rate, cost, name, at } importes del tarifario en el momento
//                      de elegir la partida. Así un cambio posterior del tarifario no
//                      altera en silencio un presupuesto ya hecho: la app avisa y
//                      deja actualizarlo con un clic.
//   tag                etiqueta de gasto
//   unitCostEstimate   coste unitario estimado por la IA (solo sin tarifa)
//   unitCostManual     coste unitario fijado a mano (prevalece)
//   unitPriceManual    tarifa unitaria fijada a mano (prevalece)
//   discountPrice      reducción/descuento sobre la tarifa, en %
//   discountCost       reducción/descuento sobre el coste, en %
//   origin             'pliego' | 'implicito' | 'manual'
//   source, rationale  de dónde sale la partida y cómo se ha estimado
//
// Reglas:
//   · Con tarifa:  tarifa unitaria y coste unitario salen del tarifario.
//   · Sin tarifa:  coste unitario = estimación (o manual) y
//                  tarifa unitaria = coste × EXTERNAL_COST_FACTOR (1,05).
//   · Total = Uds × tarifa × (1 − desc.%) · Total coste = Uds × coste × (1 − desc.%)
//
// `catalog` es un índice { código → partida } (por defecto el tarifario estático
// de budget-catalog.js; la app y las funciones pasan el de la base de datos).
import { TARIFF_BY_ID, EXTERNAL_COST_FACTOR } from './budget-catalog.js';

export function toNum(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function factor(pct) {
  const p = toNum(pct);
  return p == null ? 1 : 1 - p / 100;
}

const same = (a, b) => (a == null && b == null) || (a != null && b != null && Math.abs(a - b) < 0.00005);

// Tarifa del catálogo con importe utilizable (las que no tienen importe se estiman).
export function pricedTariff(tariffId, catalog = TARIFF_BY_ID) {
  const t = tariffId ? catalog[tariffId] : null;
  return t && toNum(t.rate) != null && toNum(t.cost) != null ? t : null;
}

// Foto de los importes de una partida del tarifario para guardarla en la fila.
export function tariffSnapshot(tariffId, catalog = TARIFF_BY_ID) {
  const t = tariffId ? catalog[tariffId] : null;
  if (!t) return null;
  return { rate: toNum(t.rate), cost: toNum(t.cost), name: t.name || '', at: new Date().toISOString() };
}

// Importes de tarifa que aplican a la fila: la foto guardada o, si no la hay
// (presupuestos anteriores), el tarifario vigente.
function tariffAmounts(row, catalog) {
  if (!row.tariffId) return { t: null, live: null, rateStale: false, costStale: false };
  const live = catalog[row.tariffId] || null;
  const snap = row.tariffSnap && typeof row.tariffSnap === 'object' ? row.tariffSnap : null;
  const src = snap || live;
  const rate = src ? toNum(src.rate) : null;
  const cost = src ? toNum(src.cost) : null;
  const t = rate != null && cost != null ? { rate, cost } : null;
  const rateStale = !!(snap && live && !same(toNum(live.rate), rate));
  const costStale = !!(snap && live && !same(toNum(live.cost), cost));
  return { t, live, rateStale, costStale };
}

// costSource / priceSource: 'tarifario' | 'estimacion' | 'manual' | 'calculado' | 'pendiente'
export function computeRow(row, catalog = TARIFF_BY_ID) {
  const { t, live, rateStale, costStale } = tariffAmounts(row, catalog);
  const units = toNum(row.units) ?? 0;

  const costManual = toNum(row.unitCostManual);
  const estimate = toNum(row.unitCostEstimate);
  let unitCost = null;
  let costSource = 'pendiente';
  if (costManual != null) { unitCost = costManual; costSource = 'manual'; }
  else if (t) { unitCost = t.cost; costSource = 'tarifario'; }
  else if (estimate != null) { unitCost = estimate; costSource = 'estimacion'; }

  const priceManual = toNum(row.unitPriceManual);
  let unitPrice = null;
  let priceSource = 'pendiente';
  if (priceManual != null) { unitPrice = priceManual; priceSource = 'manual'; }
  else if (t) { unitPrice = t.rate; priceSource = 'tarifario'; }
  else if (unitCost != null) { unitPrice = unitCost * EXTERNAL_COST_FACTOR; priceSource = 'calculado'; }

  const total = unitPrice != null ? units * unitPrice * factor(row.discountPrice) : 0;
  const totalCost = unitCost != null ? units * unitCost * factor(row.discountCost) : 0;
  return {
    units, unitCost, unitPrice, total, totalCost, costSource, priceSource,
    // La foto difiere del tarifario vigente (solo cuenta si ese importe se está usando).
    priceStale: rateStale && priceSource === 'tarifario',
    costStale: costStale && costSource === 'tarifario',
    tariffStale: (rateStale && priceSource === 'tarifario') || (costStale && costSource === 'tarifario'),
    liveRate: live ? toNum(live.rate) : null,
    liveCost: live ? toNum(live.cost) : null,
  };
}

export function computeTotals(rows, catalog = TARIFF_BY_ID) {
  let total = 0;
  let totalCost = 0;
  let pending = 0;
  let stale = 0;
  const byTag = {};
  for (const r of rows || []) {
    const c = computeRow(r, catalog);
    total += c.total;
    totalCost += c.totalCost;
    if (c.costSource === 'pendiente') pending += 1;
    if (c.tariffStale) stale += 1;
    const k = r.tag || 'Sin etiqueta';
    byTag[k] = byTag[k] || { total: 0, totalCost: 0, count: 0 };
    byTag[k].total += c.total;
    byTag[k].totalCost += c.totalCost;
    byTag[k].count += 1;
  }
  const margin = total - totalCost;
  return { total, totalCost, margin, marginPct: total > 0 ? (margin / total) * 100 : null, pending, stale, byTag };
}
